import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { fehlendeFunktion, upgradeAnbieten } from '../../billtoro/upgrade';
import { Dialog } from '../../components/Dialog';
import { useToast } from '../../lib/toast';
import { ChipRadios } from '../ChipRadios';
import { lieferantenUrl, lieferantSuche, type LieferantenAntwort, type LieferantZeile } from '../kontakte/LieferantDaten';
import { istDrittland } from '../laender-welt';
import { darf, fehlerText, TarifMarke } from '../tarifHinweis';
import { VorschlagInput } from '../Vorschlaege';
import { JahrWahl, type SeitenProps } from './Steuer';
import {
  ausBrutto,
  basis,
  centsAus,
  centsText,
  deDatum,
  erechnungLieferant,
  euro,
  herunterladen,
  landFuer,
  STEUERART,
  ZAHLWEG,
  type Ausgabe,
  type AusgabenListe,
  type BelegAntwort,
  type Kategorie,
  type Satz,
  type Steuerart,
  type Zahlweg,
  type Zeile,
} from './daten';
import '../formular.css';

const SAETZE: Satz[] = [19, 7, 0];
type Filter = 'alle' | 'ausgabe' | 'einnahme' | 'offen';

// Ausgaben und Einnahmen ohne Rechnung (/rechnung/steuer): Liste eines Jahres mit Summen, Beleg erfassen (Foto, PDF oder
// E-Rechnung; eine E-Rechnung füllt das Formular von selbst), ändern, stornieren (GoBD: nichts wird gelöscht).
// Ausgaben für Waren und Material lassen sich ins Lager übernehmen (Link zur Lagerseite).
export function Ausgaben({ data, fehltAusgaben }: SeitenProps) {
  // Team-Mitglied ohne Recht „Ausgaben und Kasse“: nur ansehen (der Server prüft)
  const schreiben = darf(data.tarif, 'ausgaben');
  // „Als Lieferant speichern“ nur mit dem Recht „Kontakte, Leistungen und Lager“; Vorschläge gibt es für alle
  const lieferantPflegen = darf(data.tarif, 'kunden');
  // „Ins Lager übernehmen“ (Paket 3, /rechnung/lager?zugang=): Waren und Material, mit Recht „Kontakte, Leistungen und
  // Lager“ und Lager im Tarif (der Server prüft beim Buchen alles noch einmal)
  const insLager = lieferantPflegen && !fehlendeFunktion(data.tarif, 'lager');
  const toast = useToast();
  const [jahr, setJahr] = useState(() => Number(data.today.slice(0, 4)));
  const [liste, setListe] = useState<AusgabenListe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('alle');
  const [q, setQ] = useState('');
  const [form, setForm] = useState<Ausgabe | 'neu' | null>(null);
  const [storno, setStorno] = useState<Ausgabe | null>(null);

  const laden = useCallback(async () => {
    try {
      setListe(await api.get<AusgabenListe>(`${basis(data.firm.id)}/ausgaben?jahr=${jahr}`));
      setError(null);
    } catch (err) {
      setError(errorText(err));
    }
  }, [data.firm.id, jahr]);
  useEffect(() => {
    void laden();
  }, [laden]);

  const kat = useMemo(() => new Map((liste?.kategorien ?? []).map((k) => [k.id, k])), [liste]);
  const aktiv = (liste?.ausgaben ?? []).filter((a) => !a.storniert);
  const summe = (r: 'ausgabe' | 'einnahme') => aktiv.filter((a) => a.richtung === r).reduce((s, a) => s + a.brutto, 0);
  const vorsteuer = aktiv.filter((a) => a.richtung === 'ausgabe' && a.steuerart === 'normal').reduce((s, a) => s + a.steuer, 0);
  const t = q.trim().toLowerCase();
  const gezeigt = (liste?.ausgaben ?? []).filter(
    (a) =>
      (filter === 'alle' || (filter === 'offen' ? !a.bezahltAm && !a.storniert : a.richtung === filter)) &&
      (!t || `${a.partner} ${a.text ?? ''} ${a.belegnummer ?? ''} ${a.nummer}`.toLowerCase().includes(t)),
  );

  function neu() {
    if (fehltAusgaben) {
      upgradeAnbieten(data.tarif, 'ausgaben');
      return;
    }
    setForm('neu');
  }

  return (
    <>
      <div className="rt-head">
        <div>
          <h1>Ausgaben</h1>
          <p className="muted">Belege fotografieren oder hochladen. E-Rechnungen liest Billtoro selbst.</p>
        </div>
        <div className="rt-head-act">
          <JahrWahl jahr={jahr} heute={data.today} onChange={setJahr} />
          {schreiben && (
            <button type="button" className={`btn primary${fehltAusgaben ? ' rt-gesperrt' : ''}`} disabled={fehltAusgaben === 'bald'} onClick={neu}>
              + Beleg erfassen
              <TarifMarke tarif={data.tarif} fehlt={fehltAusgaben} />
            </button>
          )}
        </div>
      </div>

      {error && <div className="notice bad">{error}</div>}
      {!liste ? (
        !error && <div className="spinner" aria-label="Lädt" />
      ) : (
        <>
          <div className="ag-kacheln">
            <div className="ag-kachel">
              <span>Ausgaben {jahr}</span>
              <b className="num">{euro(summe('ausgabe'))}</b>
            </div>
            {!liste.kleinunternehmer && (
              <div className="ag-kachel">
                <span>Davon Vorsteuer</span>
                <b className="num">{euro(vorsteuer)}</b>
              </div>
            )}
            <div className="ag-kachel">
              <span>Einnahmen ohne Rechnung</span>
              <b className="num">{euro(summe('einnahme'))}</b>
            </div>
          </div>

          {liste.ausgaben.length > 0 && (
            <div className="rt-tools">
              <ChipRadios<Filter>
                label="Zeigen"
                value={filter}
                options={[
                  ['alle', 'Alle'],
                  ['ausgabe', 'Ausgaben'],
                  ['einnahme', 'Einnahmen'],
                  ['offen', 'Noch nicht bezahlt'],
                ]}
                onChange={setFilter}
              />
              <input type="search" className="rt-search" value={q} placeholder="Suchen" aria-label="Ausgaben suchen" onChange={(e) => setQ(e.target.value)} />
            </div>
          )}

          {liste.ausgaben.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>Noch keine Ausgaben in {jahr}. Fotografieren Sie Ihren ersten Beleg oder laden Sie eine E-Rechnung hoch.</p>
              {schreiben && (
                <button type="button" className="btn primary" disabled={fehltAusgaben === 'bald'} onClick={neu}>
                  Ersten Beleg erfassen
                </button>
              )}
            </div>
          ) : gezeigt.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>Hier passt nichts.</p>
            </div>
          ) : (
            <ul className="ag-liste">
              {gezeigt.map((a) => (
                <li key={a.id} className={`ag-zeile${a.storniert ? ' ag-storniert' : ''}`}>
                  <span className="ag-datum num">{deDatum(a.belegdatum)}</span>
                  <span className="ag-was">
                    <b>{a.partner}</b>
                    <span className="muted">
                      {a.nummer} · {kat.get(a.kategorie)?.name ?? a.kategorie}
                      {a.text ? ` · ${a.text}` : ''}
                    </span>
                  </span>
                  <span className="ag-status">
                    {a.storniert ? (
                      <span className="badge b-none" title={a.storniert.grund}>
                        storniert
                      </span>
                    ) : a.bezahltAm ? (
                      <span className="badge b-ok">{a.zahlweg === 'bar' ? 'bar bezahlt' : a.zahlweg === 'privat' ? 'privat bezahlt' : 'bezahlt'}</span>
                    ) : (
                      <span className="badge b-warn">offen</span>
                    )}
                  </span>
                  <span className={`ag-betrag num${a.richtung === 'einnahme' ? ' ag-plus' : ''}`}>
                    {a.richtung === 'einnahme' ? '+' : '−'}
                    {euro(Math.abs(a.brutto))}
                  </span>
                  <span className="ag-act">
                    {a.beleg && (
                      <button
                        type="button"
                        className="linkbtn"
                        onClick={() => void herunterladen(`${basis(data.firm.id)}/ausgaben/${a.id}/beleg`, a.beleg!.name).catch((e) => toast(errorText(e)))}
                      >
                        Beleg
                      </button>
                    )}
                    {insLager && a.kategorie === 'wareneinkauf' && a.richtung === 'ausgabe' && !a.storniert && a.brutto > 0 && (
                      <Link to={`/rechnung/lager?zugang=${a.id}`} className="linkbtn">
                        Ins Lager übernehmen
                      </Link>
                    )}
                    {!a.storniert && !fehltAusgaben && schreiben && (
                      <>
                        <button type="button" className="linkbtn" onClick={() => setForm(a)}>
                          Ändern
                        </button>
                        <button type="button" className="linkbtn rt-danger" onClick={() => setStorno(a)}>
                          Stornieren
                        </button>
                      </>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {form && liste && (
        <AusgabeForm
          firmId={data.firm.id}
          heute={data.today}
          kategorien={liste.kategorien}
          kleinunternehmer={liste.kleinunternehmer}
          lieferantPflegen={lieferantPflegen}
          alt={form === 'neu' ? null : form}
          onClose={() => setForm(null)}
          onSaved={async (a) => {
            setForm(null);
            toast(form === 'neu' ? `${a.nummer} gespeichert` : `${a.nummer} geändert`);
            const j = Number(a.belegdatum.slice(0, 4));
            if (j !== jahr) setJahr(j);
            else await laden();
          }}
        />
      )}
      <StornoDialog
        titel={storno ? `${storno.nummer} stornieren?` : ''}
        text="Die Ausgabe bleibt sichtbar, zählt aber nicht mehr. War sie bar bezahlt, bucht das Kassenbuch sie zurück."
        offen={!!storno}
        onClose={() => setStorno(null)}
        onStorno={async (grund) => {
          await api.post(`${basis(data.firm.id)}/ausgaben/${storno!.id}/storno`, { grund });
          toast(`${storno!.nummer} storniert`);
          setStorno(null);
          await laden();
        }}
      />
    </>
  );
}

// Storno mit Grund (Pflicht)
export function StornoDialog({
  titel,
  text,
  offen,
  onClose,
  onStorno,
}: {
  titel: string;
  text: string;
  offen: boolean;
  onClose(): void;
  onStorno(grund: string): Promise<void>;
}) {
  const [grund, setGrund] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (offen) {
      setGrund('');
      setError(null);
    }
  }, [offen]);
  async function los(e: FormEvent) {
    e.preventDefault();
    if (!grund.trim()) {
      setError('Bitte kurz den Grund eintragen.');
      return;
    }
    setBusy(true);
    try {
      await onStorno(grund.trim());
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={offen} onClose={onClose} title={titel} className="ag-dlg-klein">
      <form onSubmit={(e) => void los(e)} noValidate>
        <p className="muted">{text}</p>
        <label className="field">
          <span>Grund</span>
          <input value={grund} maxLength={200} placeholder="z. B. doppelt erfasst" onChange={(e) => setGrund(e.target.value)} />
        </label>
        {error && <div className="notice bad">{error}</div>}
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn primary danger-bg" disabled={busy}>
            Stornieren
          </button>
        </div>
      </form>
    </Dialog>
  );
}

// ---------- Formular ----------

interface Betrag {
  key: number;
  satz: Satz;
  betrag: string;
  // Steuer von Hand (aus der E-Rechnung oder geändert); leer: rechnet die App
  steuer: string;
}
let schluessel = 1;
const leererBetrag = (satz: Satz = 19): Betrag => ({ key: schluessel++, satz, betrag: '', steuer: '' });

interface Werte {
  richtung: 'ausgabe' | 'einnahme';
  partner: string;
  partnerUstid: string;
  belegdatum: string;
  belegnummer: string;
  text: string;
  kategorie: string;
  steuerart: Steuerart;
  betraege: Betrag[];
  zahlweg: Zahlweg;
  bezahlt: boolean;
  bezahltAm: string;
  nutzungsdauer: string;
  // Lieferant aus der Lieferantenliste (nur bei Ausgaben); partner bleibt der Name vom Beleg
  lieferantId: string | null;
}

// Betrag: bei „normal“ und „ohne“ brutto, bei „Ich schulde die Steuer“ und EU-Ware netto (auf der Rechnung steht keine
// Umsatzsteuer, die App rechnet sie dazu)
function zeileAus(b: Betrag, steuerart: Steuerart): Zeile | undefined | null {
  const betrag = centsAus(b.betrag);
  if (betrag === undefined) return undefined;
  if (betrag === null) return null;
  if (steuerart === 'ohne') return { satz: 0, nettoCents: betrag, steuerCents: 0 };
  if (steuerart === '13b' || steuerart === 'eu_erwerb') {
    return { satz: b.satz, nettoCents: betrag, steuerCents: Math.sign(betrag) * Math.round((Math.abs(betrag) * b.satz) / 100) };
  }
  const steuer = b.steuer.trim() ? centsAus(b.steuer) : null;
  if (steuer === undefined) return undefined;
  if (steuer === null) return ausBrutto(betrag, b.satz);
  return { satz: b.satz, nettoCents: betrag - steuer, steuerCents: steuer };
}

function werteAus(a: Ausgabe | null, heute: string, kategorien: Kategorie[]): Werte {
  if (!a) {
    return {
      richtung: 'ausgabe',
      partner: '',
      partnerUstid: '',
      belegdatum: heute,
      belegnummer: '',
      text: '',
      kategorie: '',
      steuerart: 'normal',
      betraege: [leererBetrag()],
      zahlweg: 'bank',
      bezahlt: true,
      bezahltAm: heute,
      nutzungsdauer: '',
      lieferantId: null,
    };
  }
  const netto = a.steuerart === '13b' || a.steuerart === 'eu_erwerb';
  return {
    richtung: a.richtung,
    partner: a.partner,
    partnerUstid: a.partnerUstid ?? '',
    belegdatum: a.belegdatum,
    belegnummer: a.belegnummer ?? '',
    text: a.text ?? '',
    kategorie: kategorien.some((k) => k.id === a.kategorie) ? a.kategorie : '',
    steuerart: a.steuerart,
    betraege: a.zeilen.map((z) => ({
      key: schluessel++,
      satz: z.satz,
      betrag: centsText(netto ? z.nettoCents : z.nettoCents + z.steuerCents),
      steuer: netto || a.steuerart === 'ohne' ? '' : centsText(z.steuerCents),
    })),
    zahlweg: a.zahlweg,
    bezahlt: !!a.bezahltAm,
    bezahltAm: a.bezahltAm ?? heute,
    nutzungsdauer: a.nutzungsdauer ? String(a.nutzungsdauer) : '',
    lieferantId: a.lieferant?.id ?? null,
  };
}

function AusgabeForm({
  firmId,
  heute,
  kategorien,
  kleinunternehmer,
  lieferantPflegen,
  alt,
  onClose,
  onSaved,
}: {
  firmId: string;
  heute: string;
  kategorien: Kategorie[];
  kleinunternehmer: boolean;
  lieferantPflegen: boolean;
  alt: Ausgabe | null;
  onClose(): void;
  onSaved(a: Ausgabe): Promise<void>;
}) {
  const [w, setW] = useState<Werte>(() => werteAus(alt, heute, kategorien));
  const [beleg, setBeleg] = useState<BelegAntwort['beleg'] | null>(alt?.beleg ?? null);
  const [erechnung, setErechnung] = useState<unknown>(alt?.erechnung ?? null);
  const [hinweise, setHinweise] = useState<string[]>([]);
  const [laedt, setLaedt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mehr, setMehr] = useState(() => !!alt && (alt.steuerart !== 'normal' || alt.zeilen.length > 1));
  const datei = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const vonId = useId();
  // Lieferantenliste für die Vorschläge; treffer: passender Lieferant aus der E-Rechnung (erst nach „Übernehmen“
  // verknüpft)
  const [lieferanten, setLieferanten] = useState<LieferantZeile[]>([]);
  const [treffer, setTreffer] = useState<string | null>(null);
  const [lfBusy, setLfBusy] = useState(false);
  const [lfFehler, setLfFehler] = useState<string | null>(null);
  const [lfDoppelt, setLfDoppelt] = useState<string | null>(null);

  useEffect(() => {
    // Fehler still: dann eben ohne Vorschläge
    api.get<LieferantenAntwort>(lieferantenUrl(firmId)).then(
      (r) => setLieferanten(r.lieferanten),
      () => setLieferanten([]),
    );
  }, [firmId]);

  const set = (p: Partial<Werte>) => setW((v) => ({ ...v, ...p }));
  const gewaehlt: { nummer: number; name: string } | null = w.lieferantId
    ? (lieferanten.find((l) => l.id === w.lieferantId) ?? (alt?.lieferant?.id === w.lieferantId ? alt.lieferant : null))
    : null;
  const trefferL = treffer ? lieferanten.find((l) => l.id === treffer) : undefined;
  const gleicherName = (name: string) => name.trim().toLowerCase();
  const alsLieferantZeigen =
    lieferantPflegen &&
    w.richtung === 'ausgabe' &&
    !w.lieferantId &&
    !trefferL &&
    !!w.partner.trim() &&
    !lieferanten.some((l) => gleicherName(l.name) === gleicherName(w.partner));

  // Lieferant aus der Liste: Name und (in der EU) USt-IdNr. übernehmen. Außerhalb der EU ist vatId eine Steuernummer
  // und gehört nicht in die USt-IdNr. der Ausgabe.
  function waehlen(id: string) {
    const l = lieferanten.find((x) => x.id === id);
    if (!l) return;
    set({ lieferantId: l.id, partner: l.name, partnerUstid: !istDrittland(l.country) && l.vatId ? l.vatId : w.partnerUstid });
    setTreffer(null);
    setLfDoppelt(null);
    setLfFehler(null);
  }

  // ein anderer Name löst den Lieferanten
  function partnerTippen(v: string) {
    set({ partner: v, ...(w.lieferantId && gleicherName(v) !== gleicherName(gewaehlt?.name ?? '') ? { lieferantId: null } : {}) });
    setLfDoppelt(null);
    setLfFehler(null);
  }

  // Lieferant aus dem Namen (und der E-Rechnung: USt-IdNr., IBAN, Land) anlegen und verknüpfen; die Ausgabe bleibt offen
  async function alsLieferant(trotzdem = false) {
    const e = erechnungLieferant(erechnung);
    const ust = w.partnerUstid.trim() || e?.ustId || null;
    const country = landFuer(e?.land ?? null, ust);
    const body = { name: w.partner.trim(), country, vatId: istDrittland(country) ? null : ust, iban: e?.iban ?? null, trotzdem };
    setLfBusy(true);
    try {
      const r = await api.post<{ id: string; nummer: number }>(lieferantenUrl(firmId), body);
      const neu = await api.get<LieferantenAntwort>(lieferantenUrl(firmId));
      setLieferanten(neu.lieferanten);
      set({ lieferantId: r.id });
      setLfDoppelt(null);
      setLfFehler(null);
      toast(`Lieferant Nr. ${r.nummer} gespeichert`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'doppelt') setLfDoppelt(err.message);
      else {
        const t = fehlerText(err);
        setLfFehler(t ? `Lieferant nicht gespeichert: ${t}` : null);
      }
    } finally {
      setLfBusy(false);
    }
  }
  const kats = kategorien.filter((k) => k.richtung === w.richtung);
  const nettoEingabe = w.steuerart === '13b' || w.steuerart === 'eu_erwerb';
  const ohne = w.steuerart === 'ohne' || !!kategorien.find((k) => k.id === w.kategorie)?.ohneSteuer;
  const zeilen = w.betraege.map((b) => zeileAus(b, ohne ? 'ohne' : w.steuerart));
  const gueltig = zeilen.filter((z): z is Zeile => !!z);
  const summe = gueltig.reduce((s, z) => s + z.nettoCents + (nettoEingabe ? 0 : z.steuerCents), 0);
  const steuer = gueltig.reduce((s, z) => s + z.steuerCents, 0);

  function kategorieWaehlen(id: string) {
    const k = kategorien.find((x) => x.id === id);
    // Steuersatz der Kategorie vorschlagen, solange nur ein leerer Betrag da ist
    const ein = w.betraege.length === 1 && !w.betraege[0]!.betrag.trim();
    set({ kategorie: id, ...(k && ein ? { betraege: [{ ...w.betraege[0]!, satz: k.satz }] } : {}) });
  }

  async function hochladen(f: File) {
    setLaedt(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      const r = await api.post<BelegAntwort>(`${basis(firmId)}/ausgaben/beleg`, fd);
      setBeleg(r.beleg);
      setErechnung(r.erechnung);
      const v = r.vorschlag;
      if (v) {
        const netto = v.steuerart === '13b' || v.steuerart === 'eu_erwerb';
        set({
          partner: v.partner ?? w.partner,
          // ein vorher gewählter Lieferant passt nicht mehr zum Namen aus der E-Rechnung
          ...(w.lieferantId && v.lieferantId !== w.lieferantId ? { lieferantId: null } : {}),
          partnerUstid: v.partnerUstid ?? w.partnerUstid,
          belegnummer: v.belegnummer ?? w.belegnummer,
          belegdatum: v.belegdatum ?? w.belegdatum,
          steuerart: v.steuerart,
          betraege: v.zeilen.map((z) => ({
            key: schluessel++,
            satz: z.satz,
            betrag: centsText(netto ? z.nettoCents : z.nettoCents + z.steuerCents),
            steuer: netto ? '' : centsText(z.steuerCents),
          })),
        });
        if (v.steuerart !== 'normal' || v.zeilen.length > 1) setMehr(true);
        setHinweise(['E-Rechnung gelesen. Bitte noch die Kategorie wählen und prüfen.', ...v.hinweise]);
        // Lieferant aus der Liste nur anbieten, nie von selbst verknüpfen
        setTreffer(v.lieferantId && v.lieferantId !== w.lieferantId ? v.lieferantId : null);
        setLfDoppelt(null);
        setLfFehler(null);
      } else setHinweise([]);
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setLaedt(false);
      if (datei.current) datei.current.value = '';
    }
  }

  async function speichern(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!w.kategorie) return setError('Bitte die Kategorie wählen.');
    if (zeilen.some((z) => z === undefined)) return setError('Bitte die Beträge prüfen, z. B. 49,90.');
    if (!gueltig.length) return setError('Bitte den Betrag eintragen.');
    const nd = w.kategorie === 'anlage' ? Number(w.nutzungsdauer) : null;
    if (w.kategorie === 'anlage' && (!Number.isInteger(nd) || nd! < 1 || nd! > 50)) return setError('Bitte die Nutzungsdauer in Jahren eintragen (z. B. 3 für einen Laptop).');
    const body = {
      richtung: w.richtung,
      belegdatum: w.belegdatum,
      bezahltAm: w.zahlweg !== 'bank' || w.bezahlt ? w.bezahltAm : null,
      zahlweg: w.zahlweg,
      partner: w.partner,
      partnerUstid: w.partnerUstid.trim() || null,
      belegnummer: w.belegnummer.trim() || null,
      text: w.text.trim() || null,
      kategorie: w.kategorie,
      steuerart: ohne ? 'ohne' : w.steuerart,
      zeilen: gueltig,
      nutzungsdauer: nd,
      fileId: beleg?.id ?? null,
      lieferantId: w.richtung === 'ausgabe' ? w.lieferantId : null,
      erechnung: erechnung ?? null,
    };
    setBusy(true);
    try {
      const r = alt
        ? await api.put<{ ausgabe: Ausgabe }>(`${basis(firmId)}/ausgaben/${alt.id}`, body)
        : await api.post<{ ausgabe: Ausgabe }>(`${basis(firmId)}/ausgaben`, body);
      await onSaved(r.ausgabe);
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  const setBetrag = (key: number, p: Partial<Betrag>) => set({ betraege: w.betraege.map((b) => (b.key === key ? { ...b, ...p } : b)) });

  return (
    <Dialog open onClose={onClose} title={alt ? `${alt.nummer} ändern` : 'Beleg erfassen'} className="ag-dlg">
      <form onSubmit={(e) => void speichern(e)} noValidate>
        <div className="ag-upload">
          <input
            ref={datei}
            type="file"
            accept="image/*,application/pdf,.xml,application/xml,text/xml"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void hochladen(f);
            }}
          />
          {beleg ? (
            <span className="ag-beleg">
              📎 {beleg.name}{' '}
              <button type="button" className="linkbtn" onClick={() => setBeleg(null)}>
                entfernen
              </button>
            </span>
          ) : (
            <span className="muted">Foto, PDF oder E-Rechnung (XRechnung, ZUGFeRD)</span>
          )}
          <button type="button" className="btn small" disabled={laedt} onClick={() => datei.current?.click()}>
            {laedt ? 'Lädt …' : beleg ? 'Anderer Beleg' : 'Beleg hochladen'}
          </button>
        </div>
        {hinweise.length > 0 && (
          <div className="notice ok">
            {hinweise.map((h) => (
              <div key={h}>{h}</div>
            ))}
          </div>
        )}
        {w.richtung === 'ausgabe' && trefferL && (
          <div className="notice ok">
            Passt zum Lieferanten Nr. {trefferL.nummer} „{trefferL.name}“.{' '}
            <button type="button" className="linkbtn" onClick={() => waehlen(trefferL.id)}>
              Übernehmen
            </button>
          </div>
        )}
        {alsLieferantZeigen && !lfDoppelt && (
          <p className="ag-lf">
            <button type="button" className="btn small" disabled={lfBusy} onClick={() => void alsLieferant()}>
              Als Lieferant speichern
            </button>
          </p>
        )}
        {lfDoppelt && (
          <div className="notice warn">
            {lfDoppelt}{' '}
            <button type="button" className="linkbtn" disabled={lfBusy} onClick={() => void alsLieferant(true)}>
              Trotzdem speichern
            </button>
          </div>
        )}
        {lfFehler && <div className="notice bad">{lfFehler}</div>}

        {!alt && (
          <ChipRadios<'ausgabe' | 'einnahme'>
            label="Art"
            value={w.richtung}
            options={[
              ['ausgabe', 'Ausgabe'],
              ['einnahme', 'Einnahme ohne Rechnung'],
            ]}
            onChange={(r) => {
              set({ richtung: r, kategorie: '', steuerart: 'normal', lieferantId: null });
              setTreffer(null);
            }}
          />
        )}

        <div className="ag-grid ag-oben">
          {w.richtung === 'ausgabe' ? (
            // nicht in einem label (Liste zum Anklicken); der Name kommt über aria-labelledby
            <div className="field">
              <span id={vonId}>Von wem?</span>
              <VorschlagInput
                value={w.partner}
                onChange={partnerTippen}
                vorschlaege={
                  w.lieferantId
                    ? []
                    : lieferantSuche(lieferanten, w.partner).map((l) => ({
                        key: l.id,
                        label: (
                          <>
                            <span className="kw-nr num">{l.nummer}</span> {l.name}
                          </>
                        ),
                        sub: [l.vatId, `${l.zip} ${l.city}`.trim()].filter(Boolean).join(' · '),
                      }))
                }
                onPick={waehlen}
                inputProps={{ 'aria-labelledby': vonId, maxLength: 120, placeholder: 'z. B. Baumarkt Nord' }}
              />
              {gewaehlt && (
                <small>
                  Lieferant Nr. {gewaehlt.nummer}{' '}
                  <button type="button" className="linkbtn" onClick={() => set({ lieferantId: null })}>
                    lösen
                  </button>
                </small>
              )}
            </div>
          ) : (
            <label className="field">
              <span>Von wem bekommen?</span>
              <input value={w.partner} maxLength={120} placeholder="z. B. Barverkauf" onChange={(e) => set({ partner: e.target.value })} />
            </label>
          )}
          <label className="field">
            <span>Datum auf dem Beleg</span>
            <input type="date" value={w.belegdatum} max={heute} onChange={(e) => set({ belegdatum: e.target.value })} />
          </label>
        </div>

        <label className="field">
          <span>Wofür?</span>
          <select value={w.kategorie} onChange={(e) => kategorieWaehlen(e.target.value)}>
            <option value="">Bitte wählen</option>
            {kats.map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
          </select>
          {w.kategorie === 'anlage' && <small>Über 800 € netto: Billtoro verteilt die Kosten über die Nutzungsdauer.</small>}
          {w.kategorie === 'gwg' && <small>Bis 800 € netto: zählt sofort ganz als Ausgabe.</small>}
          {w.kategorie === 'bewirtung' && <small>Geschäftsessen zählt zu 70 %. Bitte auf dem Beleg notieren, mit wem und warum.</small>}
        </label>
        {w.kategorie === 'anlage' && (
          <label className="field ag-schmal">
            <span>Nutzungsdauer in Jahren</span>
            <input value={w.nutzungsdauer} inputMode="numeric" placeholder="z. B. 3" onChange={(e) => set({ nutzungsdauer: e.target.value })} />
          </label>
        )}

        {mehr && !ohne && w.richtung === 'ausgabe' && (
          <label className="field">
            <span>Umsatzsteuer auf dem Beleg</span>
            <select value={w.steuerart} onChange={(e) => set({ steuerart: e.target.value as Steuerart })}>
              {STEUERART.map(([id, text]) => (
                <option key={id} value={id}>
                  {text}
                </option>
              ))}
            </select>
          </label>
        )}

        <div className="ag-betraege">
          {w.betraege.map((b, i) => {
            const z = zeilen[i];
            return (
              <div key={b.key} className="ag-betrag-zeile">
                <label className="field">
                  <span>{nettoEingabe ? 'Betrag netto' : 'Betrag'}</span>
                  <input value={b.betrag} inputMode="decimal" placeholder="0,00" aria-invalid={z === undefined} onChange={(e) => setBetrag(b.key, { betrag: e.target.value, steuer: '' })} />
                </label>
                {!ohne && (
                  <label className="field">
                    <span>Steuersatz</span>
                    <select value={String(b.satz)} onChange={(e) => setBetrag(b.key, { satz: Number(e.target.value) as Satz, steuer: '' })}>
                      {SAETZE.map((s) => (
                        <option key={s} value={s}>
                          {s} %
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {!ohne && !nettoEingabe && (
                  <label className="field">
                    <span>davon Steuer</span>
                    <input
                      value={b.steuer || (z ? centsText(z.steuerCents) : '')}
                      inputMode="decimal"
                      onChange={(e) => setBetrag(b.key, { steuer: e.target.value })}
                    />
                  </label>
                )}
                {w.betraege.length > 1 && (
                  <button type="button" className="linkbtn ag-weg" aria-label="Betrag entfernen" onClick={() => set({ betraege: w.betraege.filter((x) => x.key !== b.key) })}>
                    ✕
                  </button>
                )}
              </div>
            );
          })}
          <div className="ag-summe">
            {!ohne && w.betraege.length < 3 && (
              <button type="button" className="linkbtn" onClick={() => set({ betraege: [...w.betraege, leererBetrag(7)] })}>
                + weiterer Steuersatz
              </button>
            )}
            {!mehr && !ohne && w.richtung === 'ausgabe' && (
              <button type="button" className="linkbtn" onClick={() => setMehr(true)}>
                Besondere Umsatzsteuer
              </button>
            )}
            <span className="num">
              {nettoEingabe ? `Netto ${euro(summe)} · Steuer ${euro(steuer)}, die Sie schulden und zugleich abziehen` : `Summe ${euro(summe)}`}
              {!nettoEingabe && !ohne && steuer !== 0 && ` · davon Steuer ${euro(steuer)}`}
            </span>
          </div>
          {kleinunternehmer && <small className="muted">Als Kleinunternehmer zählt immer der ganze Betrag.</small>}
        </div>

        {(w.steuerart === '13b' || w.steuerart === 'eu_erwerb') && (
          <label className="field">
            <span>USt-IdNr. des Lieferanten</span>
            <input value={w.partnerUstid} maxLength={20} placeholder="z. B. ATU12345678" onChange={(e) => set({ partnerUstid: e.target.value })} />
          </label>
        )}

        <div className="field">
          <span>Bezahlt</span>
          <ChipRadios<Zahlweg> label="Bezahlt" value={w.zahlweg} options={ZAHLWEG} onChange={(z) => set({ zahlweg: z, bezahlt: true })} />
        </div>
        <div className="ag-grid">
          {w.zahlweg === 'bank' && (
            <label className="ag-check">
              <input type="checkbox" checked={w.bezahlt} onChange={(e) => set({ bezahlt: e.target.checked })} />
              <span>Schon bezahlt</span>
            </label>
          )}
          {(w.zahlweg !== 'bank' || w.bezahlt) && (
            <label className="field">
              <span>{w.richtung === 'einnahme' ? 'Erhalten am' : 'Bezahlt am'}</span>
              <input type="date" value={w.bezahltAm} max={heute} onChange={(e) => set({ bezahltAm: e.target.value })} />
            </label>
          )}
        </div>
        {w.zahlweg === 'bar' && <small className="muted ag-klein">Bar bezahlt bucht Billtoro von selbst ins Kassenbuch.</small>}

        <div className="ag-grid">
          <label className="field">
            <span>Belegnummer (optional)</span>
            <input value={w.belegnummer} maxLength={60} onChange={(e) => set({ belegnummer: e.target.value })} />
          </label>
          <label className="field">
            <span>Notiz (optional)</span>
            <input value={w.text} maxLength={300} placeholder="z. B. Druckerpapier" onChange={(e) => set({ text: e.target.value })} />
          </label>
        </div>

        {error && <div className="notice bad">{error}</div>}
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn primary" disabled={busy || laedt}>
            Speichern
          </button>
        </div>
      </form>
    </Dialog>
  );
}
